import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from 'assets/icons/close.svg?react';
import styles from './Modal.module.css';
import useKeyDownPress from 'hooks/useKeyPress';

interface ModalProps {
    children?: React.ReactNode;
    title?: string;
    icon?: React.ReactNode;
    type?: 'standard' | 'fullSizeImage' | 'fullSizeImageFixed';
    align?: 'center' | 'top';
    onClose?: () => void;
}

export interface ModalRef {
    openModal: () => void;
    closeModal: () => void;
}

const Modal = forwardRef<ModalRef | undefined, ModalProps>(({ type = 'standard', align = 'center', ...props }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const isFullSizeImageModal = type === 'fullSizeImage' || type === 'fullSizeImageFixed';

    function openModal() {
        document.body.style.overflow = 'hidden';
        setIsOpen(true);
    }

    function closeModal() {
        document.body.removeAttribute('style');
        if (props.onClose) props.onClose();
        setIsOpen(false);
    }

    function handleOverlayClick(event: React.MouseEvent<HTMLDivElement, MouseEvent>) {
        if (event.target === event.currentTarget) {
            closeModal();
        }
    }

    useImperativeHandle(ref, () => ({
        openModal,
        closeModal,
    }));

    useKeyDownPress('Escape', closeModal);

    if (!isOpen) {
        return null;
    }

    const modalNode = (
        <div
            className={`${styles.overlay} ${isFullSizeImageModal ? styles.fullSizeOverlay : ''} ${
                align === 'top' ? styles.alignTop : ''
            }`}
            onClick={handleOverlayClick}
        >
            <div className={`${styles.wrapper} ${styles[type]}`}>
                <div className={styles.header}>
                    <div className={styles.headerRightSide}>
                        {props.icon && <div className={styles.iconWrapper}>{props.icon}</div>}
                        <h1 className={styles.title}>{props.title}</h1>
                    </div>
                    <button className={styles.closeButton} onClick={closeModal}>
                        <CloseIcon className={styles.closeIcon} />
                    </button>
                </div>
                <div className={styles.content}>{props.children}</div>
            </div>
        </div>
    );

    if (isFullSizeImageModal && typeof document !== 'undefined') {
        return createPortal(modalNode, document.body);
    }

    return modalNode;
});

export default Modal;
